import { APIErrorResponse } from '../serviceHelpers/APIErrorResponse';
import JSONResponseHandler from '../serviceHelpers/JSONResponseHandler';

import { SignUpResponse, SignUpProfile, isSignUpResponse } from './userProfile';
import Identity from './identity';
import { headers } from './constants';
import enforceRecaptcha, { RecaptchaMethod } from '../utils/enforceRecaptcha';

/**
 * Sign up a user for a new account. The first two parameters are complex objects (detailed below). Passing `doLogin` as `true` will automatically call `.login()` with the provided userName and password.
 *
 * @param identity
 * @param profile
 * @param rememberMe requires `doLogin === true`. If true, the login session will be persistent
 *
 *
 * @returns
 * A __Promise__ which resolves to the uuid, identity and profile and acessTokens of the user.  The `userProfile` and `userIdentity` properties will be updated with this data.
 *
 * <details>
 * <summary>
 * On success (resolves)
 * </summary>
 *  See documentation for corresponding response here:
 *  https://redirector.arcpublishing.com/alc/docs/swagger/?url=./arc-products/arc-identity-v1.json#/User_Registration/signUp
 *
 * </details>
 *
 * <details>
 * <summary>
 * On error (throws)
 * </summary>
 *
 * One of:
 *
 * ```
 * See documentation for corresponding error codes and success response:
 * https://redirector.arcpublishing.com/alc/docs/swagger/?url=./arc-products/arc-identity-v1.json#/User_Registration/signUp
 * ```
 * </details>
 */

export default function signUp(
  identity: { userName: string; credentials: string },
  profile: SignUpProfile,
  _?: boolean,
  rememberMe?: boolean,
  recaptchaToken?: string
): Promise<APIErrorResponse | SignUpResponse> {
  return enforceRecaptcha(RecaptchaMethod.SIGN_UP, recaptchaToken)
    .then(() =>
      fetch(`${Identity.apiOrigin}/identity/public/v1/signup`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          identity,
          profile,
          recaptchaToken
        })
      })
    )
    .then(JSONResponseHandler)
    .then(json => {
      if (isSignUpResponse(json)) {
        Identity._isSession = !rememberMe;
        const { identities, profile, uuid, ...userIdentity } = json;

        Identity.userProfile = { ...profile, identities, uuid };
        Identity.userIdentity = { uuid, ...userIdentity };

        return json;
      } else {
        throw json;
      }
    });
}
